import type { ActionButtonsProps } from '@app/src/components/ActionButtons'
import ActionButtons from '@app/src/components/ActionButtons'
import { usePageBreadcrumbs } from '@app/src/components/Navigation/Breadcrumbs/usePageBreadcrumbs'
import type { TabsProps } from '@app/src/components/Tabs'
import Tabs from '@app/src/components/Tabs'
import type { SustainabilityLibraryParams } from '@app/src/pages/Configurations/SustainabilityLibraryScene'
import paths from '@app/src/wf-constants/paths'
import { Box, Stack, Typography } from '@mui/material'
import type { PropsWithChildren, ReactNode } from 'react'
import React from 'react'
import { Helmet } from 'react-helmet'
import { useIntl } from 'react-intl'
import { Redirect, generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom'

const DEFAULT_PADDING: EnablePaddingProps = {
  top: true,
  right: false,
  bottom: true,
  left: true,
}

type EnablePaddingProps = {
  top?: boolean
  right?: boolean
  bottom?: boolean
  left?: boolean
}

type SustainabilityLibraryCollectionProps = {
  title?: string
  secondaryTitle?: ReactNode
  titleTags?: ReactNode
  actionTabs?: TabsProps['tabs']
  actionButtons?: ActionButtonsProps['buttons']
  secondaryAction?: ReactNode
  enablePadding?: EnablePaddingProps
}

const SustainabilityLibraryCollection: React.FC<PropsWithChildren<SustainabilityLibraryCollectionProps>> = ({
  title,
  secondaryTitle,
  titleTags,
  actionTabs,
  actionButtons,
  secondaryAction,
  children,
  enablePadding,
}) => {
  const paddingEnabled: EnablePaddingProps = {
    top: enablePadding?.top ?? DEFAULT_PADDING.top,
    right: enablePadding?.right ?? DEFAULT_PADDING.right,
    bottom: enablePadding?.bottom ?? DEFAULT_PADDING.bottom,
    left: enablePadding?.left ?? DEFAULT_PADDING.left,
  }
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { path } = useRouteMatch()
  const { search } = useLocation()
  const { configurationsPage, configurationsSubPage, activeTabParam } = useParams<SustainabilityLibraryParams>()

  const sustainabilityLibraryCrumb = {
    label: formatMessage({ id: 'configurations.sustainabilityLibrary' }),
    to: paths.sustainabilityLibrary,
  }
  const resolvedPageTitle = formatMessage({ id: `configurations.${configurationsPage}` })
  const resolvedTitle = title ?? resolvedPageTitle
  const pageCrumb = {
    label: resolvedPageTitle,
    to: generatePath(paths.sustainabilityLibrary, { configurationsPage }),
  }
  const pageWithSubPageCrumb = {
    label: resolvedTitle,
    to: generatePath(paths.sustainabilityLibrary, { configurationsPage, configurationsSubPage }) + search,
  }
  const hasEntitySubPage = Boolean(configurationsSubPage && Number(configurationsSubPage))
  const activeSubTab = actionTabs?.find(t => t.type === activeTabParam)

  const isStandardQuestionnaire = new URLSearchParams(search).get('isStandard') === 'true'
  const isQuestionnaireTab = activeTabParam === 'questionnaire'

  let subTabCrumb = null
  if (activeSubTab && typeof activeSubTab.label === 'string') {
    const label =
      isStandardQuestionnaire && isQuestionnaireTab
        ? formatMessage({ id: 'questionnaireConfig.preview' })
        : activeSubTab.label
    subTabCrumb = {
      label,
      to:
        generatePath(paths.sustainabilityLibrary, {
          configurationsPage,
          configurationsSubPage,
          activeTabParam: activeSubTab.url,
        }) + (activeSubTab.skipQueryParams ? '' : search),
    }
  } else if (activeTabParam && isStandardQuestionnaire && isQuestionnaireTab) {
    subTabCrumb = {
      label: formatMessage({ id: 'questionnaireConfig.preview' }),
      to:
        generatePath(paths.sustainabilityLibrary, { configurationsPage, configurationsSubPage, activeTabParam }) +
        search,
    }
  }

  const breadcrumbs = [sustainabilityLibraryCrumb, pageCrumb]

  if (hasEntitySubPage) {
    breadcrumbs.push(pageWithSubPageCrumb)
  }

  if (subTabCrumb) {
    breadcrumbs.push(subTabCrumb)
  }

  usePageBreadcrumbs(breadcrumbs)

  const handleTabChange = (value: string): void => {
    const selectedTab = actionTabs?.find(t => t.type === value)

    if (selectedTab) {
      history.push({
        pathname: generatePath(path, {
          configurationsPage,
          configurationsSubPage,
          activeTabParam: selectedTab.url,
        }),
        search: selectedTab.skipQueryParams ? undefined : search,
      })
    }
  }

  if (actionTabs && !activeTabParam) {
    return (
      <Redirect
        to={{
          pathname: generatePath(path, {
            configurationsPage,
            configurationsSubPage,
            activeTabParam: actionTabs[0].url,
          }),
          search,
        }}
      />
    )
  }

  return (
    <Box flexGrow={1} display="flex" flexDirection="column" overflow="hidden" height="100%">
      <Helmet>
        <title>{title ?? formatMessage({ id: `configurations.${configurationsPage}` })}</title>
      </Helmet>

      <Box px={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Stack direction="row" spacing={2}>
            <Typography variant="h1">
              {title ?? formatMessage({ id: `configurations.${configurationsPage}` })}
            </Typography>
            {titleTags}
          </Stack>
          <Stack direction="row" spacing={1}>
            {secondaryAction}
            <ActionButtons buttons={actionButtons ?? []} />
          </Stack>
        </Box>
        {secondaryTitle}
        {actionTabs && (
          <Box mt={1}>
            <Tabs activeTab={activeTabParam} tabs={actionTabs} handleChange={(_, value) => handleTabChange(value)} />
          </Box>
        )}
      </Box>

      <Box
        mt={!actionTabs ? 4 : 0}
        pl={paddingEnabled.left ? 4 : 0}
        pr={paddingEnabled.right ? 4 : 0}
        bgcolor="grey.100"
        flexGrow={1}
        display="flex"
        flexDirection="column"
      >
        <Box
          pt={paddingEnabled.top ? 4 : 0}
          pb={paddingEnabled.bottom ? 4 : 0}
          flexGrow={1}
          display="flex"
          flexDirection="column"
        >
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default SustainabilityLibraryCollection
